"use client";

import React from "react";
import {
  MapPin,
  Scale,
  AlertTriangle,
  FileText,
  BookOpen,
  CheckCircle,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@ui/accordion";
import { Separator } from "@ui/separator";

const commitments = [
  "Quality education and continuous curriculum improvement",
  "Ethical practice and professional responsibility",
  "International collaboration and recognition",
  "The advancement of naturopathic and natural medicine in Nigeria and beyond",
];

export function LegalNotices() {
  return (
    <section className="py-20 px-4 bg-background">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-14 animate-[fadeUp_0.5s_ease-out_both]">
          <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Regulations, Licensing &{" "}
            <span className="bg-gradient-to-r from-healing-600 to-emerald-600 bg-clip-text text-transparent">
              Important Notices
            </span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Transparency is central to our values. Please review the following
            important information carefully to understand our institutional
            framework.
          </p>
        </div>

        <Accordion type="multiple" className="space-y-4">
          {/* Scope of Practice */}
          <AccordionItem
            value="scope"
            className="border border-border rounded-xl px-6 overflow-hidden bg-card transition-all duration-200"
          >
            <AccordionTrigger className="py-5 hover:no-underline focus:ring-0 focus-visible:ring-0 outline-none group data-[state=open]:bg-transparent data-[state=active]:bg-transparent select-none">
              <div className="flex items-center gap-4 text-left">
                <div className="w-10 h-10 rounded-lg bg-healing-100 flex items-center justify-center shrink-0 group-hover:bg-healing-200 transition-colors">
                  <MapPin className="w-5 h-5 text-healing-700" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Scope of Practice & Regulation
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Jurisdiction-specific information
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-6">
              <p className="text-muted-foreground text-sm leading-relaxed">
                Scope of practice varies by jurisdiction. Students benefit from
                training in Lagos, where a broad range of naturopathic therapies
                are practiced. Graduates are trained to operate ethically within
                applicable legal and professional boundaries.
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* Licensing */}
          <AccordionItem
            value="licensing"
            className="border border-border rounded-xl px-6 overflow-hidden bg-card transition-all hover:border-wisdom-200"
          >
            <AccordionTrigger className="py-5 hover:no-underline group">
              <div className="flex items-center gap-4 text-left">
                <div className="w-10 h-10 rounded-lg bg-wisdom-100 flex items-center justify-center shrink-0 group-hover:bg-wisdom-200 transition-colors">
                  <Scale className="w-5 h-5 text-wisdom-700" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Licensing & Professional Responsibility
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Licensing obligations for graduates
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-6">
              <p className="text-muted-foreground text-sm leading-relaxed">
                Licensing requirements vary by state and country. Graduates are
                responsible for confirming local regulations. OICNM provides
                education aligned with professional naturopathic standards but
                does not guarantee licensure in all jurisdictions.
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* Institutional Disclaimer */}
          <AccordionItem
            value="disclaimer"
            className="border-2 border-destructive/20 rounded-xl px-6 overflow-hidden bg-destructive/[0.02]"
          >
            <AccordionTrigger className="py-5 hover:no-underline group">
              <div className="flex items-center gap-4 text-left">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0 group-hover:bg-destructive/20 transition-colors">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Institutional Status & Accreditation Disclaimer
                  </h3>
                  <p className="text-xs mt-0.5 font-medium text-destructive">
                    Important — please read before enrolling
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-6 space-y-4 text-sm text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">
                  OICNM is not accredited by the National Universities
                  Commission (NUC) of Nigeria.
                </strong>{" "}
                This is by design and by institutional classification, and
                should not be interpreted as a shortcoming, illegality, or lack
                of educational credibility.
              </p>
              <p>
                OICNM is not a university, nor is it structured as a
                conventional academic college offering standard university
                degrees regulated by the NUC (such as B.Sc., M.Sc., or Ph.D.).
                Instead, OICNM operates as a{" "}
                <strong className="text-foreground">
                  Professional, Clinical, and Entrepreneurial College of Natural
                  Medicine
                </strong>
                , dedicated to training competent practitioners in naturopathic
                and integrative healthcare through practical, skills-based, and
                profession-focused education.
              </p>
              <p>
                OICNM was established to bridge the gap between theory and
                real-world practice in natural medicine. This model reflects the
                global tradition of naturopathic and complementary medicine
                education, which in many countries exists outside the
                conventional university framework.
              </p>
              <p>
                OICNM is recognized by the{" "}
                <strong className="text-foreground">
                  World Naturopathic Federation (WNF)
                </strong>
                , an internationally respected body representing naturopathic
                medicine and institutions worldwide. This recognition affirms
                OICNM&apos;s alignment with international naturopathic
                principles, professional identity, and educational objectives.
              </p>

              <Separator className="my-6" />

              <div>
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-healing-600" />{" "}
                  Qualifications Awarded
                </h4>
                <p>
                  All qualifications awarded by OICNM are{" "}
                  <strong className="text-foreground">
                    professional certifications, diplomas, or professional
                    doctorates
                  </strong>{" "}
                  — not conventional university degrees. OICNM programs are
                  designed primarily for professional practice and
                  entrepreneurship. Admission into conventional postgraduate
                  university programs may require additional assessments as
                  determined by the receiving institution.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <p className="text-xs text-muted-foreground italic">
                  By applying to or enrolling in OICNM, students acknowledge
                  that they fully understand and accept the institution&apos;s
                  professional status, accreditation position, and scope of
                  qualification.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-wisdom-600" /> OICNM&apos;s
                  Commitment
                </h4>
                <ul className="space-y-2">
                  {commitments.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-healing-600 mt-0.5 shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>
  );
}
